import React, { useState, useEffect, useLayoutEffect, useMemo, useRef } from "react";
import { Box, Typography, Chip, Paper, Button, Stack, IconButton, Card, TextField } from "@mui/material";
import { useParams, useNavigate } from "react-router-dom";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import SearchIcon from "@mui/icons-material/Search";
import KeyboardArrowUpIcon from "@mui/icons-material/KeyboardArrowUp";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import CloseIcon from "@mui/icons-material/Close";
import DownloadIcon from "@mui/icons-material/Download";
import ImageIcon from "@mui/icons-material/Image";
import PictureAsPdfIcon from "@mui/icons-material/PictureAsPdf";
import QrCode2Icon from "@mui/icons-material/QrCode2";
import DownloadForOfflineIcon from "@mui/icons-material/DownloadForOffline";
import LinkIcon from "@mui/icons-material/Link";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import WorkspacePremiumIcon from "@mui/icons-material/WorkspacePremium";
import TwoWheelerIcon from "@mui/icons-material/TwoWheeler";
import PersonOutlineIcon from "@mui/icons-material/PersonOutline";
import CurrencyRupeeIcon from "@mui/icons-material/CurrencyRupee";
import PeopleOutlineIcon from "@mui/icons-material/PeopleOutline";
import AccountBalanceOutlinedIcon from "@mui/icons-material/AccountBalanceOutlined";
import VerifiedUserOutlinedIcon from "@mui/icons-material/VerifiedUserOutlined";
import EventNoteOutlinedIcon from "@mui/icons-material/EventNoteOutlined";
import FolderOpenOutlinedIcon from "@mui/icons-material/FolderOpenOutlined";
import TimelineIcon from "@mui/icons-material/Timeline";
import DescriptionOutlinedIcon from "@mui/icons-material/DescriptionOutlined";
import moment from "moment";
import {
  isNational,
  hasCompanyLeg,
  sameCompany,
  BRISK,
  describeCompanies,
  describePaCompany,
  splitFromPolicy,
  describeParts,
  existingTpModeOfPolicy,
  tpDetailsOpen,
  existingTpInsurerOf,
} from "./utils/companyRules";
import Loader from "../../../UiComponents/Loader/Loader";
import { GetOnlinePolicy, GetDocumentPresignedUrl, getBriskV2DownloadUrl, getBriskV2PolicyCertificates } from "../../../Service/_index";
import { onPolicyEvent } from "../../../Service/realtime";
import { ToastError } from "../../../UiComponents/Toaster/Toast";
import { downloadPolicyPdf } from "./utils/policyPdf";
import { financierView } from "./utils/financierView";
import MasterChangedNotice from "./components/MasterChangedNotice";
import AutomationStatusCard from "./components/AutomationStatusCard";
import RelianceStageList from "./components/RelianceStageList";
import { hasStageView } from "./components/RelianceStagesHoverCard";
import KycFailedNotice from "./components/KycFailedNotice";
import PaymentQrCard from "./components/PaymentQrCard";
import { PRIMARY_BUTTON_SX, ROW_BUTTON_SX } from "./components/formStyles";
import "./components/viewOnlinePolicy.scss";
import { qrEndsAt } from "./utils/paymentQr";
import { paymentChipOf, isWaitingForMoney, requiredText } from "./utils/paymentHold";
import { hasRealParts, realParts, shortLabel, insurerColor } from "./utils/policyParts";
import { payableRowsOf } from "./utils/insurerPayable";
import { shownPaymentLink } from "./utils/paymentLinkView";
import { statusColor, statusLabel } from "../BriskCertificateList/briskV2ListUtils";
import { tpAutoText } from "./utils/tpDates";

/**
 * Map a policy onto the Brisk Create Certificate form's field names.
 *
 * Mirrors the payload the automation sends (relianceForm.js
 * createBriskCertificate) — the shape verified against a real success. Only
 * the fields that page prefills are produced here; Make/Model are passed as
 * plain text and left for its own dropdowns to resolve against Brisk's
 * catalogue, because sending an insurer's own spelling is exactly what used to
 * make Brisk fail with a bare 500.
 */
const NAVY = "#1E3A5F";

// The page's colours are the navbar's and its frame is the History page's (the owner, 2026-10-30): a navy band, a pale
// body, white sections with a navy-tinted title. Colour is otherwise kept for what it means (a status, a warning).
const BAND_GRADIENT = "linear-gradient(135deg, #1E3A5F 0%, #2E5A8F 100%)";
const TINT = "#EEF3F9";
const SECTION_LINE = "#DCE4EE";
const ON_BAND = "rgba(255,255,255,0.78)";
// Everything on the band reads white on navy, so a chip says its meaning with a dot (like the History page's Live chip):
// a filled or default chip from the theme is dark text on navy there.
const BAND_CHIP_SX = { height: 22, fontSize: 11.5, fontWeight: 600, color: "#fff", backgroundColor: "rgba(255,255,255,0.12)", border: "1px solid rgba(255,255,255,0.28)" };
const PAY_DOT = { success: "#32D583", warning: "#FEC84B", error: "#FDA29B", default: "rgba(255,255,255,0.7)" };
// The search box and both buttons share one height, so their edges line up.
const BAND_CONTROL_HEIGHT = 36;

// One side of the page. Large screens: it fills the height and scrolls on its own; smaller screens: part of the page.
// No padding on top: the title of a section stops at the very top, and rows must not show above it.
const PANE_SX = {
  minWidth: 0,
  minHeight: 0,
  pr: { md: 0.5 },
  overflowY: { md: "auto" },
  overscrollBehavior: "contain", // reaching the end of one side moves nothing else
  scrollbarWidth: "thin",
  scrollbarColor: "#C9D7E8 transparent",
  scrollbarGutter: { md: "stable" }, // nothing shifts when a live update makes a side long enough to scroll
};
// The columns follow the room the side really has, so they line up from one section to the next at any window size.
const FIELD_GRID_SX = { display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(min(180px, 100%), 1fr))", columnGap: 1, rowGap: 0.25, p: 0.75 };

const mapPolicyToBriskFields = (policy) => {
  if (!policy) return {};

  // Brisk expects the PA cover premium split at 18% GST, net = total / 1.18 —
  // the same arithmetic the Brisk UI page does.
  const totalPremium = Number(policy.paCoverAmount) || 244;
  const netPremium = Number((totalPremium / 1.18).toFixed(2));
  const gst = Number((totalPremium - netPremium).toFixed(2));

  const titleCase = (value) =>
    value
      ? String(value).charAt(0).toUpperCase() + String(value).slice(1).toLowerCase()
      : "";

  const join = (...parts) => parts.filter(Boolean).join(" ").trim();

  return {
    CustomerName: join(policy.fullName, policy.surname),
    MobileNo: policy.mobileNumber || "",
    EmailID: policy.email || "",
    City: policy.RTOCity || policy.areaAndLocality || "",
    State: policy.state || "",
    // Brisk accepts "Male"/"Female", not the form's lower-case values.
    CustomerGender: titleCase(policy.gender),
    CustomerDOB: policy.dateOfBirth || "",
    NomineeName: policy.nomineeName || "",
    NomineeGender: titleCase(policy.nomineeGender),
    Relation: titleCase(policy.nomineeRelation),
    Make: policy.vehicleMake || "",
    Model: policy.vehicleModel || "",
    EngineNo: policy.engineNumber || "",
    ChassisNo: policy.chassisNumber || "",
    RegistrationNo: policy.registrationNumber || policy.registrationNo || "",
    Address_Line1: join(policy.flatDoorNo, policy.buildingName),
    Address_Line2: join(policy.roadStreetLane, policy.areaAndLocality),
    totalPremium: String(totalPremium),
    netPremium: String(netPremium),
    gst: String(gst),
    policyType: "CPA/RSA",
  };
};

/**
 * ViewOnlinePolicy Component
 * Displays complete policy information in a beautiful single-page view
 */
const ViewOnlinePolicy = ({ onBack }) => {
  const { policyId } = useParams();
  const navigate = useNavigate();
  const [policyData, setPolicyData] = useState(null);
  const [loading, setLoading] = useState(true);
  // Presigned URLs fetched fresh from the backend for image rendering
  const [docUrls, setDocUrls] = useState({});

  useEffect(() => {
    fetchPolicyData();
  }, [policyId]);

  // Live policy updates (2026-10-22): when the server says THIS policy changed (or the socket came back after a break),
  // read it again quietly - debounced, so a burst of changes is one read.
  useEffect(() => {
    let timer = null;
    const off = onPolicyEvent((e) => {
      if (!(e.type === "resync" || (e.type === "changed" && String(e.policyId) === String(policyId)))) return;
      clearTimeout(timer);
      timer = setTimeout(() => fetchPolicyData({ quiet: true, live: true }), 1000);
    });
    return () => {
      off();
      clearTimeout(timer);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [policyId]);

  // Whenever the PAN / Aadhaar files change, fetch presigned URLs for them so they render. Keyed on the files, not on
  // every read of the policy, so a live refresh does not ask for the same two links again.
  const panKey = policyData?.panCard?.key;
  const aadharKey = policyData?.aadharCard?.key;
  useEffect(() => {
    if (!policyData) return;
    const fetchUrls = async () => {
      const keys = {
        panCard: policyData.panCard?.key,
        aadharCard: policyData.aadharCard?.key,
      };
      const results = {};
      for (const [name, key] of Object.entries(keys)) {
        if (key) {
          const res = await GetDocumentPresignedUrl(key);
          if (!res.error && res.presignedUrl) results[name] = res.presignedUrl;
        }
      }
      setDocUrls(results);
    };
    fetchUrls();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [panKey, aadharKey, !!policyData]);

  // Where the PA certificate stands: our own saved record, not a call to Brisk. A cancelled one says so on its file row.
  const briskNumber = policyData?.briskCertificate?.certificateNo;
  const [briskStatus, setBriskStatus] = useState("");
  useEffect(() => {
    if (!briskNumber) {
      setBriskStatus("");
      return undefined;
    }
    let alive = true;
    (async () => {
      try {
        const res = await getBriskV2PolicyCertificates(policyId);
        const record = !res.error && ((res.data && res.data.records) || []).find((r) => r.certificateNo === briskNumber);
        if (alive) setBriskStatus((record && record.status) || "");
      } catch (e) {
        /* only a label: the file row works without it */
      }
    })();
    return () => {
      alive = false;
    };
    // updatedAt: a live update of the policy reads it again
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [policyId, briskNumber, policyData?.updatedAt]);

  // quiet = a background refresh: no full-page loader, and a failure keeps the page
  // live = a live-update read: a failure says nothing (the page keeps what it shows)
  const fetchPolicyData = async ({ quiet = false, live = false } = {}) => {
    try {
      if (!quiet) setLoading(true);
      const response = live ? await GetOnlinePolicy(policyId, { silent: true }) : await GetOnlinePolicy(policyId);

      if (response && !response.error && response.data) {
        setPolicyData(response.data);
      } else if (live) {
        // keep the page as it is
      } else {
        ToastError("Failed to load policy data");
        if (!quiet) navigate("/onlinePolicy");
      }
    } catch (error) {
      console.error("Error fetching policy:", error);
      if (live) return;
      ToastError("Error loading policy data");
      if (!quiet) navigate("/onlinePolicy");
    } finally {
      if (!quiet) setLoading(false);
    }
  };

  const formatDate = (date) => {
    if (!date) return "-";
    if (date instanceof Date) {
      return moment(date).format("DD/MM/YYYY");
    }
    if (typeof date === "string") {
      const parsed = new Date(date);
      if (Number.isNaN(parsed.getTime())) {
        return "-";
      }
      return moment(parsed).format("DD/MM/YYYY");
    }
    return moment(date).format("DD/MM/YYYY");
  };

  const [downloading, setDownloading] = useState(false);

  // Where the page's sticky bar should park: directly under the app navbar,
  // never over it. Measured rather than hardcoded — the shell stacks one or
  // two fixed bars depending on the screen, so a fixed 60px would be wrong
  // half the time.
  const [stickyTop, setStickyTop] = useState(0);
  // Where the page's frame starts, so it can fill the rest of the window exactly. Measured for the same reason: the
  // breadcrumb above it changes size with the screen.
  const frameRef = useRef(null);
  const [frameTop, setFrameTop] = useState(0);
  useLayoutEffect(() => {
    const measure = () => {
      if (frameRef.current) setFrameTop(Math.round(frameRef.current.getBoundingClientRect().top + window.scrollY));
    };
    measure();
    window.addEventListener("resize", measure);
    // a web font arriving late can change the breadcrumb's height
    if (document.fonts?.ready) document.fonts.ready.then(measure);
    return () => window.removeEventListener("resize", measure);
  }, [loading]);
  useEffect(() => {
    const measure = () => {
      const bar = document.querySelector(".MainAppBar");
      if (!bar) return setStickyTop(0);
      const pinned = ["fixed", "sticky"].includes(
        window.getComputedStyle(bar).position
      );
      // A navbar that scrolls away with the page needs no offset at all.
      if (!pinned) return setStickyTop(0);

      const rect = bar.getBoundingClientRect();
      // For a pinned bar, its bottom edge IS the offset from the viewport top,
      // and it already accounts for a second stacked row. Falling back to the
      // height keeps this correct if it is measured mid-scroll.
      const offset = Math.round(rect.bottom) || Math.round(rect.height);
      setStickyTop(offset > 0 ? offset : 60);
    };
    measure();
    window.addEventListener("resize", measure);
    return () => window.removeEventListener("resize", measure);
    // Re-measured once the content replaces the loader: the shell can lay out
    // differently between the two, and a stale offset shows as a bar sitting
    // over the navbar.
  }, [loading]);

  const handleDownloadPdf = async () => {
    if (!policyData) return;
    setDownloading(true);
    try {
      // Built from the policy data rather than a screenshot of the page, so
      // the text stays selectable and nothing depends on what is scrolled
      // into view. docUrls carries the presigned document links this page has
      // already resolved — the PDF embeds those images and links to them.
      // The policy's own files (policy PDF, proposal PDF, Brisk certificate) get a FRESH link each, made now: they are
      // listed in the PDF as Download links. A file that has no link is simply left out.
      const freshUrl = async (file) => {
        try {
          const key = getDocumentKey(file);
          if (key) {
            const res = await GetDocumentPresignedUrl(key);
            if (!res.error && res.presignedUrl) return res.presignedUrl;
          }
        } catch (e) {
          /* fall back to what is stored */
        }
        return getDocumentUrl(file) || "";
      };
      const [policyPdf, proposalPdf, briskCertificate] = await Promise.all([
        freshUrl(policyData.mergedPolicyPdf),
        freshUrl(policyData.proposalPdf),
        freshUrl(policyData.briskCertificate),
      ]);
      await downloadPolicyPdf(policyData, {
        docUrls: { ...docUrls, policyPdf, proposalPdf, briskCertificate },
        // The one link that does not expire: this policy's page, where every file has its own button.
        pageUrl: `${window.location.origin}/onlinePolicy/view/form/${policyData._id || policyId}`,
      });
    } catch (e) {
      ToastError(e.message || "Could not build the PDF");
    } finally {
      setDownloading(false);
    }
  };

  // onBack: the Online Policy screen's own reset, the same one its list header uses.
  const handleBack = () => {
    if (onBack) onBack();
    else navigate("/onlinePolicy");
  };

  const handleDownload = (url, filename) => {
    if (!url) {
      ToastError('No download URL available');
      return;
    }
    // Use a direct anchor link — S3 presigned URLs block fetch() due to CORS
    // but work perfectly as href. The `download` attribute is a hint only, and
    // a browser ignores it for another site's address: what really saves the file
    // is a link signed as an attachment (downloadDocument asks for one). That link
    // leaves this page where it is; any other link is shown by the browser, so it
    // gets a tab of its own rather than replacing this page.
    const a = document.createElement('a');
    a.href = url;
    a.download = filename || 'download';
    if (!/[?&]response-content-disposition=attachment/i.test(url)) {
      a.target = '_blank';
      a.rel = 'noopener noreferrer';
    }
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
  };


  // Get document URL (handle both string and object formats)
  // Returns the key for on-demand presigned URL generation.
  const getDocumentKey = (doc) => {
    if (!doc) return null;
    if (typeof doc === 'object' && doc.key) return doc.key;
    return null;
  };

  // Get direct URL fallback (when no key available)
  const getDocumentUrl = (doc) => {
    if (!doc) return null;
    if (typeof doc === 'string') return doc;
    if (typeof doc === 'object') {
      if (doc.presignedUrl) return doc.presignedUrl;
      if (doc.location) return doc.location;
    }
    return null;
  };

  // The PA certificate has its number but no file on the policy yet (Brisk was still making the PDF when the automation
  // tried). The server saves it to our S3 now, once, and answers with that copy; from then on the policy has it.
  const savedBriskCopy = async (doc, options) => {
    if (!doc || doc !== policyData?.briskCertificate || !doc.certificateNo) return {};
    const list = await getBriskV2PolicyCertificates(policyData._id);
    const record = !list.error && ((list.data && list.data.records) || []).find((r) => r.certificateNo === doc.certificateNo);
    if (!record) return {};
    const res = await getBriskV2DownloadUrl(record.id, options);
    if (res.error || !res.data) return { message: res.message };
    return { url: res.data.url };
  };

  // Open a document in a new tab — always fetches a fresh presigned URL from backend.
  const openDocument = async (doc) => {
    // The tab opens at the click, before the link is fetched: a browser can block a tab opened after a wait.
    const tab = window.open("", "_blank");
    if (tab) tab.opener = null;
    const key = getDocumentKey(doc);
    let url = null;
    if (key) {
      const res = await GetDocumentPresignedUrl(key, { view: true });
      if (!res.error && res.presignedUrl) url = res.presignedUrl;
    }
    // Fallback to stored URL
    if (!url) url = getDocumentUrl(doc);
    let why = "";
    if (!url) {
      const saved = await savedBriskCopy(doc);
      url = saved.url;
      why = saved.message;
    }
    if (!url) {
      if (tab) tab.close();
      ToastError(why || "Could not open document — no URL available.");
      return;
    }
    if (tab) tab.location.href = url;
    else window.open(url, "_blank", "noopener,noreferrer");
  };

  // Download a document — always fetches a fresh presigned URL from backend.
  const downloadDocument = async (doc, filename) => {
    const key = getDocumentKey(doc);
    let url = null;
    if (key) {
      const res = await GetDocumentPresignedUrl(key, { fileName: filename });
      if (!res.error && res.presignedUrl) {
        url = res.presignedUrl;
      }
    }
    if (!url) url = getDocumentUrl(doc);
    let why = "";
    if (!url) {
      const saved = await savedBriskCopy(doc, { fileName: filename });
      url = saved.url;
      why = saved.message;
    }
    if (!url) {
      ToastError(why || "Could not download document — no URL available.");
      return;
    }
    handleDownload(url, filename);
  };

  if (loading) {
    return <Loader open={loading} />;
  }

  if (!policyData) {
    return (
      <Box sx={{ p: 4, textAlign: "center" }}>
        <Typography variant="h6" color="error">
          Policy not found
        </Typography>
      </Box>
    );
  }

  // ── the summary card's figures ──
  const customerName = `${policyData.fullName || policyData.firstName || "-"} ${policyData.surname || policyData.lastName || ""}`.trim() || "-";
  const references = [
    policyData.policyNumber ? `Policy ${policyData.policyNumber}` : "",
    policyData.proposalNumber ? `Proposal ${policyData.proposalNumber}` : "",
    policyData.policyId || "",
  ].filter(Boolean);
  const vehicleLine = [
    [policyData.vehicleMake, policyData.vehicleModel].filter(Boolean).join(" "),
    policyData.vehicleVariant,
    policyData.rtoCityLocation,
  ]
    .filter(Boolean)
    .join(" · ");
  const insurerChips = hasRealParts(policyData)
    ? realParts(policyData)
        .filter((p) => !["cancelled", "skipped"].includes(String(p.status || "")))
        .map((p) => ({ key: p.key, label: `${p.label || shortLabel(p.key)} · ${p.company || "-"}`, ...insurerColor(p.company) }))
    : describeCompanies(policyData) || policyData.company
      ? [{ key: "company", label: describeCompanies(policyData) || policyData.company, ...insurerColor(policyData.company) }]
      : [];
  const payChip = paymentChipOf(policyData);
  const coverItems = [
    ["Zero Depreciation", policyData.zeroDepreciation],
    ["TPPD Restrict", policyData.tppdRestrict],
    ["PA Cover", policyData.paCover],
  ];
  const showNominee = !!(policyData.paCover || hasCompanyLeg(policyData, isNational));
  const showFinancier = !!(policyData.hasFinancier || policyData.financierName);
  const tpMode = existingTpModeOfPolicy(policyData);
  const showTp = tpMode === "required" || (tpMode === "optional" && tpDetailsOpen(policyData));
  const briskMissing = !!(policyData.paCover && sameCompany(describePaCompany(policyData), BRISK) && !policyData.briskCertificate);
  const policyFiles = [
    {
      doc: policyData.briskCertificate,
      title: "Brisk Certificate",
      subtitle: policyData.briskCertificate?.certificateNo ? `Certificate No: ${policyData.briskCertificate.certificateNo}` : "CPA / RSA cover",
      // Only the cancel states: a certificate in force needs no label. Its file stays viewable either way.
      badge: ["CANCELLING", "CANCELLED", "CANCEL_UNKNOWN"].includes(briskStatus) ? (
        <Chip
          size="small"
          data-testid="brisk-file-status"
          label={statusLabel(briskStatus)}
          sx={{ height: 22, fontSize: 11.5, fontWeight: 700, color: "#fff", backgroundColor: statusColor(briskStatus) }}
        />
      ) : null,
    },
    { doc: policyData.mergedPolicyPdf, title: "Policy PDF", subtitle: "Policy document" },
    { doc: policyData.proposalPdf, title: "Proposal PDF", subtitle: "Downloaded proposal" },
    { doc: policyData.panCard, title: "PAN Card", subtitle: policyData.panCard?.fileName || "Customer document", icon: ImageIcon, ext: "jpg" },
    { doc: policyData.aadharCard, title: "Aadhaar Card", subtitle: policyData.aadharCard?.fileName || "Customer document", icon: ImageIcon, ext: "jpg" },
    // A QR with an expiry is its own card (countdown, then "expired"); this row is for older ones.
    { doc: qrEndsAt(policyData.paymentQrScreenshot) === null ? policyData.paymentQrScreenshot : null, title: "Payment QR", subtitle: "Payment QR screenshot", icon: QrCode2Icon, ext: "png" },
  ].filter((file) => file.doc);
  const qrWithExpiry = qrEndsAt(policyData.paymentQrScreenshot) !== null;
  const paymentLink = shownPaymentLink(policyData);
  const hasLinks = !!(
    policyData.kycUrls?.ovdUrl ||
    policyData.kycUrls?.uploadUrl ||
    policyData.kycLink ||
    policyData.proposalLink ||
    paymentLink ||
    policyData.kycStatus
  );

  // The owner's page (2026-10-30), on the History page's frame: a small margin around it, a navy band, then the policy's
  // details on the left and Files, Status and Process Links on the right. Large screens: the window never scrolls and each
  // side scrolls on its own. Smaller screens: one normal page, the details first. The band holds who it is on the left;
  // search, Back to List and Download PDF on the right. The app's own padding is replaced in viewOnlinePolicy.scss.
  return (
    <Box
      ref={frameRef}
      className="policy-view-page"
      data-testid="policy-view-page"
      sx={{
        display: "flex",
        flexDirection: "column",
        // the 12px is the page's margin below the frame (viewOnlinePolicy.scss)
        height: { md: `calc(100vh - ${frameTop}px - 12px)` },
        border: "1px solid #EAECF0",
        borderRadius: "16px",
        backgroundColor: "#F8FAFC",
        // large screens: clipped to the rounded frame; smaller ones must not clip, or the search bar cannot stick
        overflow: { md: "hidden" },
      }}
    >
      {/* display: contents on a phone, so the search bar's parent is the whole page and it can stay in view */}
      <Box
        data-testid="policy-view-band"
        sx={{
          flexShrink: 0,
          display: { xs: "contents", md: "grid" },
          gridTemplateColumns: "minmax(0, 1fr) auto",
          alignItems: "center",
          background: BAND_GRADIENT,
        }}
      >
        {/* Summary: who, which policy, which vehicle, who sells it, what it costs */}
        <Box
          data-testid="view-summary"
          sx={{
            minWidth: 0,
            pl: { xs: 1.5, sm: 2 },
            pr: { xs: 1.5, sm: 2, md: 0 },
            py: 1.25,
            display: "flex",
            alignItems: "center",
            gap: 1.5,
            background: { xs: BAND_GRADIENT, md: "none" },
            borderRadius: { xs: "15px 15px 0 0", md: 0 },
          }}
        >
          <Box
            sx={{
              width: 40,
              height: 40,
              flexShrink: 0,
              borderRadius: "12px",
              display: { xs: "none", sm: "flex" },
              alignItems: "center",
              justifyContent: "center",
              color: "#fff",
              backgroundColor: "rgba(255,255,255,0.14)",
              border: "1px solid rgba(255,255,255,0.22)",
            }}
          >
            <DescriptionOutlinedIcon />
          </Box>
          <Box sx={{ minWidth: 0, flex: 1 }}>
            <Box sx={{ display: "flex", flexWrap: "wrap", alignItems: "center", columnGap: 1, rowGap: 0.5 }}>
              <Typography sx={{ fontSize: 17, fontWeight: 600, color: "#fff", lineHeight: 1.3, overflowWrap: "anywhere" }}>
                <Highlight>{customerName}</Highlight>
              </Typography>
              {insurerChips.map((c) => (
                <Chip key={c.key} size="small" label={c.label} sx={BAND_CHIP_SX} />
              ))}
              {/* Paid / not paid (the policy's payment record; older policies have none and show nothing). */}
              {payChip && (
                <Box component="span" data-testid="policy-payment">
                  <Chip
                    size="small"
                    label={payChip.label}
                    icon={<Box component="span" sx={{ width: 8, height: 8, borderRadius: "50%", backgroundColor: PAY_DOT[payChip.tone] || PAY_DOT.default, ml: "8px !important" }} />}
                    sx={BAND_CHIP_SX}
                  />
                </Box>
              )}
            </Box>
            {/* one line on large screens, so the band keeps its height; the numbers come first and the vehicle is in its section too */}
            <Typography
              title={[references.join(" · "), vehicleLine].filter(Boolean).join(" · ")}
              sx={{
                mt: 0.25,
                fontSize: 12.5,
                color: ON_BAND,
                overflowWrap: "anywhere",
                overflow: { md: "hidden" },
                textOverflow: { md: "ellipsis" },
                whiteSpace: { md: "nowrap" },
              }}
            >
              <Highlight>{[references.join(" · "), vehicleLine].filter(Boolean).join(" · ")}</Highlight>
            </Typography>
          </Box>
          {/* the line keeps the premium apart from the controls, as the navbar's does before the bell */}
          <Box sx={{ textAlign: "right", flexShrink: 0, pr: { md: 2 }, borderRight: { md: "1px solid rgba(255,255,255,0.22)" } }}>
            <Typography sx={{ fontSize: 20, fontWeight: 700, color: "#fff", lineHeight: 1.2 }}>
              ₹{Number(policyData.finalPremium || 0).toLocaleString("en-IN")}
            </Typography>
            <Typography sx={{ fontSize: 11, fontWeight: 500, color: ON_BAND }}>Final premium</Typography>
          </Box>
        </Box>

        <Stack
          data-testid="policy-view-top"
          direction={{ xs: "column", sm: "row" }}
          alignItems={{ xs: "stretch", sm: "center" }}
          justifyContent="flex-end"
          gap={1}
          sx={{
            // Phones: the window scrolls there, so search and the actions stay reachable without going back up.
            position: { xs: "sticky", md: "static" },
            top: `${stickyTop}px`,
            // Below MUI's AppBar (1100) on purpose: if the measurement is ever
            // off, the navbar covers this bar rather than the other way round.
            zIndex: 5,
            px: { xs: 1.5, sm: 2 },
            pb: { xs: 1, md: 0 },
            backgroundColor: { xs: "#1E3A5F", md: "transparent" },
          }}
        >
          <PolicySearchBox policyData={policyData} />
          <Stack direction="row" spacing={1} alignItems="center" className="no-print">
            <Button
              startIcon={<ArrowBackIcon />}
              onClick={handleBack}
              sx={{
                flex: { xs: 1, sm: "none" },
                height: BAND_CONTROL_HEIGHT,
                px: 1.5,
                fontSize: 13,
                textTransform: "none",
                whiteSpace: "nowrap",
                fontWeight: 600,
                color: "#fff",
                border: "1px solid rgba(255,255,255,0.35)",
                borderRadius: "8px",
                "&:hover": { backgroundColor: "rgba(255,255,255,0.12)", borderColor: "#fff" },
              }}
            >
              Back to List
            </Button>
            <Button
              startIcon={<DownloadForOfflineIcon />}
              onClick={handleDownloadPdf}
              disabled={downloading}
              sx={{
                flex: { xs: 1, sm: "none" },
                height: BAND_CONTROL_HEIGHT,
                px: 1.5,
                fontSize: 13,
                textTransform: "none",
                whiteSpace: "nowrap",
                fontWeight: 600,
                color: "#1E3A5F",
                backgroundColor: "#fff",
                borderRadius: "8px",
                "&:hover": { backgroundColor: "#D4AF37", color: "#fff" },
                "&.Mui-disabled": { backgroundColor: "rgba(255,255,255,0.6)", color: "#1E3A5F" },
              }}
            >
              {downloading ? "Preparing..." : "Download PDF"}
            </Button>
          </Stack>
        </Stack>
      </Box>

      {isWaitingForMoney(policyData) && (
        <Typography sx={{ flexShrink: 0, px: 2, py: 0.75, fontSize: 13, color: "#B42318", backgroundColor: "#FEF3F2", borderBottom: "1px solid #FECDCA" }}>
          Saved on hold, nothing charged yet. Remove hold charges {requiredText(policyData) || "the wallets"} and starts the automation.
          {policyData.payment?.lastError ? ` Last try: ${policyData.payment.lastError}` : ""}
        </Typography>
      )}

      <Box
        data-testid="policy-view-workspace"
        sx={{
          flex: { md: 1 },
          minHeight: 0,
          display: "grid",
          gridTemplateColumns: { xs: "minmax(0, 1fr)", md: "minmax(0, 3fr) minmax(400px, 2fr)" },
          gridTemplateRows: { md: "minmax(0, 1fr)" },
          gap: 1.5,
          p: { xs: 1, md: 1.5 },
        }}
      >
        <Box data-testid="policy-details-pane" sx={PANE_SX}>
          <Box sx={{ "&:not(:empty)": { mb: 1 } }}>
            <MasterChangedNotice
              entityType="makeModel"
              entityId={policyData.makeModel}
              since={policyData.createdAt}
            />
            {policyData.makeModelCombo && (
              <MasterChangedNotice
                entityType="makeModelCombo"
                entityId={policyData.makeModelCombo}
                since={policyData.createdAt}
              />
            )}
          </Box>

          <ViewSection icon={<TwoWheelerIcon />} title="Vehicle" fields>
            {policyData.makeModelComboName && <DetailField label="Combined Make & Model" value={policyData.makeModelComboName} wide />}
            <DetailField
              label={policyData.tpVehicle ? "OD Make & Model" : "Make & Model"}
              value={`${policyData.vehicleMake || "-"} - ${policyData.vehicleModel || "-"}`}
            />
            {policyData.tpVehicle && (
              <DetailField
                label="TP Make & Model"
                wide
                value={[
                  `${policyData.tpVehicle.vehicleMake || "-"} - ${policyData.tpVehicle.vehicleModel || "-"}`,
                  policyData.tpVehicle.vehicleVariant,
                  policyData.tpVehicle.vehicleCC ? `${policyData.tpVehicle.vehicleCC} CC` : "",
                ]
                  .filter(Boolean)
                  .join(" · ")}
              />
            )}
            <DetailField label="Company" value={describeCompanies(policyData) || policyData.company || "-"} />
            {policyData.paCover && <DetailField label="PA Cover Company" value={describePaCompany(policyData) || "-"} />}
            <DetailField label="Policy Type" value={policyData.policyType || "-"} />
            <DetailField label="CC" value={policyData.vehicleCC || "-"} />
            <DetailField label="RTO City/Location" value={policyData.rtoCityLocation || "-"} />
            <DetailField label="Manufacturing" value={[policyData.manufacturingMonth, policyData.manufacturingYear].filter(Boolean).join(" / ") || "-"} />
            <DetailField label="Engine Number" value={policyData.engineNumber || "-"} />
            <DetailField label="Chassis Number" value={policyData.chassisNumber || "-"} />
          </ViewSection>

          <ViewSection icon={<PersonOutlineIcon />} title="Customer" fields>
            <DetailField label="Full Name" value={[policyData.proposerTitle, customerName].filter(Boolean).join(" ")} double />
            {policyData.fatherName && <DetailField label="Father's Name" value={policyData.fatherName} />}
            <DetailField label="Date of Birth" value={formatDate(policyData.dateOfBirth)} />
            <DetailField
              label="Gender"
              value={policyData.gender ? policyData.gender.charAt(0).toUpperCase() + policyData.gender.slice(1) : "-"}
            />
            <DetailField label="Mobile Number" value={policyData.mobileNumber || "-"} />
            <DetailField label="Email" value={policyData.email || "-"} double />
            <DetailField
              label="Address"
              wide
              value={
                [
                  policyData.flatDoorNo,
                  policyData.floorNo,
                  policyData.buildingName,
                  policyData.blockName,
                  policyData.roadStreetLane,
                  policyData.areaAndLocality,
                  policyData.state ? policyData.state.charAt(0).toUpperCase() + policyData.state.slice(1) : "",
                  policyData.country,
                ]
                  .filter(Boolean)
                  .join(", ") + (policyData.pincode ? ` - ${policyData.pincode}` : "") || "-"
              }
            />
          </ViewSection>

          <ViewSection icon={<CurrencyRupeeIcon />} title="Premium & cover" fields>
            <DetailField label="IDV" value={`₹${policyData.idv?.toLocaleString() || "0"}`} />
            {/* Multi-company: what each insurer was paid, as stored on the policy when it was saved (premiumCalculation.split). */}
            {splitFromPolicy(policyData)?.isSplit &&
              splitFromPolicy(policyData).byCompany.map((c) => (
                <DetailField
                  key={c.company}
                  label={`${c.company} (${describeParts(c.parts)})`}
                  // `charge` is what the wallet was debited; a policy saved before it existed has only `total`.
                  value={`₹${Number(c.charge ?? c.total).toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`}
                />
              ))}
            <DetailField label="Final Premium" value={`₹${policyData.finalPremium?.toLocaleString() || "0"}`} bold />
            {/* What the insurer asked for when the policy was paid (the Reliance CDT amount) beside what we calculated and
                debited. A difference is shown as it is, never hidden (utils/insurerPayable.js). */}
            {payableRowsOf(policyData).map((row) => (
              <React.Fragment key={`payable-${row.part}`}>
                <DetailField label={`${row.label} · Calculated amount`} value={row.calculatedText} />
                <DetailField label={`${row.label} · ${row.amountLabel}`} value={row.cdtText} bold />
                <DetailField
                  label={`${row.label} · Difference`}
                  value={row.differenceText}
                  tone={row.differencePaise === 0 ? "ok" : row.differencePaise === null ? undefined : "warn"}
                />
              </React.Fragment>
            ))}
            <Box sx={{ gridColumn: "1 / -1", px: 0.75, py: 0.5 }}>
              <Typography sx={{ fontSize: 12, lineHeight: "16px", fontWeight: 500, color: "#667085", mb: 0.5 }}>Cover</Typography>
              <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.75 }}>
                {coverItems.map(([label, on]) => (
                  <Chip
                    key={label}
                    size="small"
                    label={label}
                    sx={{
                      fontWeight: 600,
                      color: on ? "#067647" : "#98A2B3",
                      backgroundColor: on ? "#ECFDF3" : "#F2F4F7",
                      textDecoration: on ? "none" : "line-through",
                    }}
                  />
                ))}
              </Box>
            </Box>
          </ViewSection>

          {showNominee && (
            <ViewSection icon={<PeopleOutlineIcon />} title="Nominee" fields>
              <DetailField label="Nominee Name" value={policyData.nomineeName || "-"} />
              <DetailField
                label="Nominee Relation"
                value={policyData.nomineeRelation ? policyData.nomineeRelation.charAt(0).toUpperCase() + policyData.nomineeRelation.slice(1) : "-"}
              />
              {hasCompanyLeg(policyData, isNational) ? (
                <DetailField label="Nominee Age" value={policyData.nomineeAge || "-"} />
              ) : (
                <DetailField label="Nominee DOB" value={formatDate(policyData.nomineeDob)} />
              )}
            </ViewSection>
          )}

          {showFinancier && (
            <ViewSection icon={<AccountBalanceOutlinedIcon />} title="Financier Details" fields>
              {financierView(policyData).showType && <DetailField label="Financier Type" value={policyData.financierType || "-"} />}
              <DetailField label="Financier Name" value={policyData.financierName || "-"} />
              <DetailField label={financierView(policyData).addressLabel} value={policyData.financierAddress || "-"} />
            </ViewSection>
          )}

          {/* The customer's existing TP policy: OD only (typed on the form) / two insurers (only once the TP part is finished) */}
          {showTp && (
            <ViewSection icon={<VerifiedUserOutlinedIcon />} title="Third party (TP) policy" fields testId="view-existing-tp">
              <DetailField label="TP Insurer" value={existingTpInsurerOf(policyData, tpMode) || "Not entered yet"} />
              <DetailField label="TP Policy Number" value={policyData.existingTpPolicyNumber || "Not entered yet"} />
              <DetailField label="TP Start Date" value={policyData.existingTpStartDate ? formatDate(policyData.existingTpStartDate) : tpMode === "optional" ? tpAutoText("start") : "Not entered yet"} />
              <DetailField label="TP End Date" value={policyData.existingTpEndDate ? formatDate(policyData.existingTpEndDate) : tpMode === "optional" ? tpAutoText("end", policyData.paCoverYears) : "Not entered yet"} />
            </ViewSection>
          )}

          <ViewSection icon={<EventNoteOutlinedIcon />} title="Dates & record" fields>
            <DetailField label="Purchase Date" value={formatDate(policyData.purchaseDate)} />
            <DetailField label="Registration Date" value={formatDate(policyData.registrationDate)} />
            <DetailField label="Created" value={formatDate(policyData.createdAt)} />
            <DetailField label="Updated" value={formatDate(policyData.updatedAt)} />
            <DetailField label="Created By" value={policyData.createdBy || "-"} />
            <DetailField label="Record Status" value={policyData.status || "-"} />
          </ViewSection>
        </Box>

        <Box data-testid="policy-files-pane" sx={PANE_SX}>
          {/* Files first (the owner, 2026-10-30). View opens a new tab, Download saves the file. */}
          <ViewSection icon={<FolderOpenOutlinedIcon />} title="Files">
            <Stack spacing={0.75}>
              {policyFiles.map((file) => {
                const FileIcon = file.icon || PictureAsPdfIcon;
                return (
                  <PanelRow key={file.title} icon={<FileIcon sx={{ fontSize: 20 }} />} title={file.title} subtitle={file.subtitle} badge={file.badge}>
                    <Button aria-label={`View ${file.title}`} startIcon={<OpenInNewIcon />} onClick={() => openDocument(file.doc)} sx={ROW_BUTTON_SX}>
                      View
                    </Button>
                    <Button
                      aria-label={`Download ${file.title}`}
                      startIcon={<DownloadIcon />}
                      onClick={() => downloadDocument(file.doc, (file.doc && file.doc.fileName) || `${file.title.replace(/\s+/g, "_")}.${file.ext || "pdf"}`)}
                      sx={ROW_BUTTON_SX}
                    >
                      Download
                    </Button>
                  </PanelRow>
                );
              })}
              {qrWithExpiry && (
                <PaymentQrCard
                  compact
                  doc={policyData.paymentQrScreenshot}
                  policyStatus={policyData.status}
                  onView={() => openDocument(policyData.paymentQrScreenshot)}
                  onDownload={() => downloadDocument(policyData.paymentQrScreenshot, policyData.paymentQrScreenshot.fileName || "Payment_QR.png")}
                />
              )}
              {/* Brisk certificate missing — offer to create it now. Shown when PA Cover is on, the provider is BRISK, and
                  no certificate is attached (keying off the missing document catches every cause). */}
              {briskMissing && (
                <PanelRow
                  icon={<WorkspacePremiumIcon sx={{ fontSize: 20 }} />}
                  color="#b54708"
                  tint="#FEF0C7"
                  tone="warn"
                  title="Brisk certificate not created"
                  subtitle="This policy has PA Cover but no CPA/RSA certificate. Create it now — the form opens filled in from this policy."
                  wrapSubtitle
                >
                  <Button
                    onClick={() =>
                      navigate("/briskCreateCertificate", {
                        state: {
                          // A DISTINCT flag, not editMode: that one also sets
                          // oldCertificateId, which makes Brisk treat the request
                          // as an amendment instead of a new certificate.
                          prefillFromPolicy: true,
                          onlinePolicyId: policyData._id,
                          certificateData: mapPolicyToBriskFields(policyData),
                        },
                      })
                    }
                    sx={{ ...PRIMARY_BUTTON_SX, height: 30, px: 1.25, fontSize: "12.5px", whiteSpace: "nowrap" }}
                  >
                    Create Certificate
                  </Button>
                </PanelRow>
              )}
              {!policyFiles.length && !qrWithExpiry && !briskMissing && (
                <Typography sx={{ fontSize: 13, color: "#667085", px: 0.75 }}>No files are available for this policy.</Typography>
              )}
            </Stack>
          </ViewSection>

          {/* Status: each part's automation and, for Reliance / KSHEMA, its three steps */}
          <ViewSection icon={<TimelineIcon />} title="Status">
            {hasRealParts(policyData) ? (
              <SearchableStatusCard parts={policyData.parts} statusLabel={policyData.statusLabel} />
            ) : (
              <DetailField label="Job Status" value={policyData.jobStatus || "Pending"} />
            )}
            {/* Reliance: the three steps (proposal / KYC / payment), read-only — they are changed from the History dialog */}
            {hasStageView(policyData) && (
              <Box data-testid="view-reliance-stages">
                <RelianceStageList
                  relianceStages={policyData.relianceStageView}
                  paymentMethod={policyData.Paymentmethod}
                  readOnly
                />
              </Box>
            )}
            {!hasRealParts(policyData) &&
              ["failed", "failed_login_form", "failed_post_submission"].includes(policyData.jobStatus) &&
              policyData.errorMessage && (
                <Paper elevation={0} sx={{ mt: 1, p: 1.5, backgroundColor: "#FFFAEB", border: "1px solid #FEC84B", borderRadius: "8px" }}>
                  <Typography variant="subtitle2" fontWeight="bold" color="error" gutterBottom>
                    Error Details
                  </Typography>
                  <Typography variant="body2" color="text.secondary">{policyData.errorMessage}</Typography>
                </Paper>
              )}
          </ViewSection>

          {/* KYC and Payment links captured by the automation. Also shown when there is a KYC note but no link was
              captured, so a failed KYC is never left unexplained. */}
          {hasLinks && (
            <ViewSection icon={<LinkIcon />} title="Process Links">
              {/* kycStatus is only set when the KYC needs a person: the portal refused it (KSHEMA), or the
                  Reliance automation did not get it through (it also leaves "KYC was started but did not
                  finish" if the run died mid-step). Shown above the links because it is the reason the
                  operator is looking at them — and, for Reliance, says to upload the document by hand. */}
              <KycFailedNotice
                kycStatus={policyData.kycStatus}
                uploadUrl={policyData.kycUrls?.uploadUrl}
                proposalNumber={policyData.proposalNumber}
                jobStatus={policyData.jobStatus}
                paymentUrl={paymentLink}
              />
              <Stack spacing={0.75}>
                {[
                  { url: policyData.kycUrls?.ovdUrl, title: "KYC Method (OVD) Page", subtitle: "Where the OVD upload method is chosen" },
                  { url: policyData.kycUrls?.uploadUrl, title: "Document Upload Page", subtitle: "Where the Aadhaar document is uploaded" },
                  {
                    // Listed before the KYC link because that is the order they
                    // happen in: the proposal is the page KYC was started from,
                    // and KYC opens in its own tab. For Reliance it is the
                    // Pending Quotes page the proposal is listed and paid from
                    // (saved whether or not the KYC worked), so the card names
                    // the proposal to look for.
                    url: policyData.proposalLink,
                    title: "Proposal Page",
                    subtitle: policyData.proposalNumber ? `Where proposal ${policyData.proposalNumber} is on the portal` : "Where this proposal is on the portal",
                  },
                  { url: policyData.kycLink, title: "KYC Verification Page", subtitle: "Continue KYC process" },
                  { url: paymentLink, title: "Make Payment Page", subtitle: "Complete the policy payment" },
                ]
                  .filter((link) => link.url)
                  .map((link) => {
                    // KYC failed and this is where the document is uploaded by hand: mark it.
                    const startHere =
                      link.title === "Document Upload Page" &&
                      Boolean(policyData.kycStatus) &&
                      policyData.jobStatus !== "processing" &&
                      policyData.jobStatus !== "completed";
                    return (
                      <PanelRow
                        key={link.title}
                        icon={<LinkIcon sx={{ fontSize: 20 }} />}
                        color={startHere ? "#b3261e" : undefined}
                        tint={startHere ? "#FEE4E2" : undefined}
                        tone={startHere ? "danger" : undefined}
                        title={link.title}
                        subtitle={link.subtitle}
                        subtitleTitle={link.url}
                        badge={startHere ? <Chip size="small" color="error" label="Start here" sx={{ fontWeight: 600 }} /> : null}
                      >
                        <Button
                          aria-label={`${link.title} - open in a new tab`}
                          startIcon={<OpenInNewIcon />}
                          onClick={() => window.open(link.url, "_blank", "noopener,noreferrer")}
                          sx={ROW_BUTTON_SX}
                        >
                          Open
                        </Button>
                      </PanelRow>
                    );
                  })}
              </Stack>
              {policyData.kycUrls?.capturedAt && (
                <Typography sx={{ fontSize: 11, color: "#98a2b3", mt: 0.75, px: 0.75 }}>
                  Captured {moment(policyData.kycUrls.capturedAt).format("DD/MM/YYYY hh:mm A")}
                </Typography>
              )}
            </ViewSection>
          )}
        </Box>
      </Box>
    </Box>
  );
};

/**
 * One section of a side of the page, as on the History page: a white card with a navy-tinted title that stays at the top
 * of its side while the section scrolls under it (large screens). `fields` = its content is label-over-value fields in
 * lined-up columns.
 */
const ViewSection = ({ icon, title, children, fields = false, testId }) => (
  // no overflow clipping here: it would stop the title from sticking
  <Box component="section" data-testid={testId} sx={{ border: `1px solid ${SECTION_LINE}`, borderRadius: "12px", backgroundColor: "#fff", "& + &": { mt: 1.5 } }}>
    <Box
      sx={{
        position: { md: "sticky" },
        top: 0,
        zIndex: 1,
        display: "flex",
        alignItems: "center",
        gap: 0.75,
        px: 1.25,
        py: 0.75,
        color: NAVY,
        backgroundColor: TINT,
        borderLeft: `4px solid ${NAVY}`,
        borderBottom: `1px solid ${SECTION_LINE}`,
        borderRadius: "11px 11px 0 0",
        "& svg": { fontSize: 18 },
      }}
    >
      {icon}
      <Typography component="h3" sx={{ fontWeight: 700, fontSize: 13.5, lineHeight: "20px", color: NAVY }}>
        {title}
      </Typography>
    </Box>
    <Box sx={fields ? FIELD_GRID_SX : { p: 1 }}>{children}</Box>
  </Box>
);

/**
 * One row of the right side: a file or a process link, with the same frame and the same edges for both.
 * tone: "danger" (the link to start from) / "warn" (a file that is missing).
 */
const ROW_TONES = {
  danger: { border: "#b3261e", background: "#fdf6f5" },
  warn: { border: "#f5c489", background: "#fffaf0" },
};
const PanelRow = ({ icon, color = NAVY, tint = TINT, title, subtitle, subtitleTitle, wrapSubtitle = false, tone, badge = null, children }) => (
  <Card
    elevation={0}
    sx={{
      p: 1,
      display: "flex",
      alignItems: "center",
      flexWrap: "wrap",
      columnGap: 1,
      rowGap: 0.75,
      borderRadius: "8px",
      border: `1px solid ${ROW_TONES[tone]?.border || "#EAECF0"}`,
      backgroundColor: ROW_TONES[tone]?.background || "#fff",
    }}
  >
    <Box sx={{ width: 36, height: 36, borderRadius: "8px", backgroundColor: tint, color, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
      {icon}
    </Box>
    <Box sx={{ flex: "1 1 110px", minWidth: 0 }}>
      <Typography sx={{ fontWeight: 600, fontSize: 13.5, lineHeight: "18px", color: "#101828" }}>{title}</Typography>
      <Typography
        title={subtitleTitle || subtitle}
        sx={{ fontSize: 11.5, lineHeight: "16px", color: "#667085", ...(wrapSubtitle ? {} : { overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }) }}
      >
        {subtitle}
      </Typography>
    </Box>
    {badge}
    <Box sx={{ display: "flex", gap: 0.75, flexShrink: 0 }}>{children}</Box>
  </Card>
);

/**
 * The active search term, shared with whatever on the page can match it.
 *
 * Held by SearchProvider, ABOVE the page, so that a new term does not redraw the page: only what reads this context is
 * redrawn (the fields, the highlighted text, the status card). Files, links and steps are left alone. Each key press used
 * to redraw the whole page two or three times (the term, the count, the position), which is what made typing lag.
 */
const SearchContext = React.createContext({ term: "", setTerm: () => {} });

const SearchProvider = ({ children }) => {
  const [term, setTerm] = useState("");
  const value = useMemo(() => ({ term, setTerm }), [term]);
  return <SearchContext.Provider value={value}>{children}</SearchContext.Provider>;
};

// How long typing must pause before the page looks for it: a fast typist's next key comes sooner than this.
const SEARCH_PAUSE_MS = 120;

/**
 * The search box. What is typed, the count and the position live here, so a key press redraws only this box and the
 * letter appears at once; the page is told when the typing pauses, or straight away on Enter or when cleared.
 */
const PolicySearchBox = ({ policyData }) => {
  const { term, setTerm } = React.useContext(SearchContext);
  const [typed, setTyped] = useState("");
  const [matchCount, setMatchCount] = useState(0);
  const [matchIndex, setMatchIndex] = useState(0);

  useEffect(() => {
    const timer = setTimeout(() => setTerm(typed), typed ? SEARCH_PAUSE_MS : 0);
    return () => clearTimeout(timer);
  }, [typed, setTerm]);

  // Matches are counted from the DOM AFTER the render that marks them — the
  // rows decide their own match state, so nothing here has to duplicate the
  // matching rule.
  useEffect(() => {
    if (!term) {
      setMatchCount(0);
      setMatchIndex(0);
      return;
    }
    const found = document.querySelectorAll('[data-search-match="true"]');
    setMatchCount(found.length);
    setMatchIndex(0);
  }, [term, policyData]);

  // At once, the way a browser's own find does: a smooth scroll restarted on every change and felt like the search lagging.
  useEffect(() => {
    if (!term || matchCount === 0) return;
    const found = document.querySelectorAll('[data-search-match="true"]');
    const target = found[Math.min(matchIndex, found.length - 1)];
    target?.scrollIntoView({ block: "center" });
  }, [term, matchIndex, matchCount]);

  const gotoMatch = (step) => {
    if (!matchCount) return;
    // Wraps in both directions, so the last match steps back to the first.
    setMatchIndex((i) => (i + step + matchCount) % matchCount);
  };

  return (
    <TextField
      size="small"
      value={typed}
      placeholder="Search this policy..."
      onChange={(e) => setTyped(e.target.value)}
      onKeyDown={(e) => {
        // Enter walks the matches, the way a browser's own find does.
        if (e.key === "Enter") {
          e.preventDefault();
          // still inside the pause: look for what is typed now, instead of walking the old matches
          if (typed !== term) setTerm(typed);
          else gotoMatch(e.shiftKey ? -1 : 1);
        }
      }}
      sx={{
        width: { xs: "100%", sm: 200, lg: 240, xl: 300 },
        backgroundColor: "#fff",
        borderRadius: "8px",
        // doubled: index.scss sets every input's height with !important
        "&& .MuiOutlinedInput-root": { borderRadius: "8px", height: `${BAND_CONTROL_HEIGHT}px !important`, minHeight: "0 !important", fontSize: 13 },
        "& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline": {
          // index.scss forces every outline transparent; restated here
          // so the box visibly has the focus.
          borderColor: "#D4AF37 !important",
          borderWidth: "2px !important",
        },
      }}
      InputProps={{
        startAdornment: <SearchIcon sx={{ fontSize: 18, color: "#667085", mr: 0.75 }} />,
        endAdornment: typed ? (
          <Stack direction="row" alignItems="center" spacing={0.25}>
            <Typography sx={{ fontSize: 11, color: matchCount ? "#667085" : "#D92D20", whiteSpace: "nowrap" }}>
              {matchCount ? `${matchIndex + 1}/${matchCount}` : "0"}
            </Typography>
            <IconButton size="small" aria-label="Previous match" disabled={!matchCount} onClick={() => gotoMatch(-1)}>
              <KeyboardArrowUpIcon sx={{ fontSize: 16 }} />
            </IconButton>
            <IconButton size="small" aria-label="Next match" disabled={!matchCount} onClick={() => gotoMatch(1)}>
              <KeyboardArrowDownIcon sx={{ fontSize: 16 }} />
            </IconButton>
            <IconButton size="small" aria-label="Clear search" onClick={() => setTyped("")}>
              <CloseIcon sx={{ fontSize: 15 }} />
            </IconButton>
          </Stack>
        ) : null,
      }}
    />
  );
};

/** Reads the term here, so a search redraws this card and not the page around it. */
const SearchableStatusCard = (props) => {
  const { term } = React.useContext(SearchContext);
  return <AutomationStatusCard bare {...props} term={term} renderValue={(text) => <Highlight>{text}</Highlight>} />;
};

/** Escape a user's typing so it cannot be read as a regular expression. */
const escapeRegExp = (value) => String(value).replace(/[.*+?^${}()|[\]\\]/g, "\\$&");

/**
 * Text with every occurrence of `term` wrapped in a marker.
 *
 * Rendered through React rather than by writing innerHTML: the values come
 * from policy data, and building markup out of them by hand is how an HTML
 * injection gets in.
 */
const Highlight = ({ children }) => {
  const { term } = React.useContext(SearchContext);
  const value = children === null || children === undefined ? "" : String(children);
  if (!term) return value;

  const parts = value.split(new RegExp(`(${escapeRegExp(term)})`, "ig"));
  return parts.map((part, i) =>
    part.toLowerCase() === term.toLowerCase() ? (
      <Box
        key={i}
        component="mark"
        sx={{
          backgroundColor: "#FEF0C7",
          color: "#101828", // it can sit on the navy band too
          px: 0.25,
          borderRadius: "3px",
        }}
      >
        {part}
      </Box>
    ) : (
      part
    )
  );
};

/**
 * One field of a section (the owner, 2026-10-30: "details showing field ui not good"): the small grey label over its
 * value, in columns that line up from section to section. `wide` takes the whole line (an address), `double` two columns
 * (an email, which would otherwise break mid-word; a phone has too little room for it). A search match is tinted and
 * carries data-search-match so the header's arrows can walk to it; `tone` colours the CDT difference.
 */
const DetailField = ({
  label,
  value,
  bold = false,
  wide = false,
  double = false,
  tone = undefined, // "ok" | "warn": green / orange (the CDT difference)
}) => {
  const { term } = React.useContext(SearchContext);
  // Matched on the label AND the value: someone looking for "chassis" wants
  // the field, someone pasting a chassis number wants the same row.
  const haystack = `${label ?? ""} ${value ?? ""}`.toLowerCase();
  const isMatch = !!term && haystack.includes(term.toLowerCase());
  const shown = value || "N/A";
  const empty = shown === "-" || shown === "N/A";

  return (
    <Box
      data-search-match={isMatch ? "true" : undefined}
      sx={{
        gridColumn: wide ? "1 / -1" : double ? { xs: "auto", sm: "span 2" } : undefined,
        minWidth: 0,
        px: 0.75,
        py: 0.5,
        borderRadius: "6px",
        scrollMarginTop: "96px", // clears the sticky header when scrolled to
        backgroundColor: isMatch ? "#FFFCF5" : tone === "ok" ? "#F6FEF9" : tone === "warn" ? "#FFFAEB" : "transparent",
        boxShadow: isMatch ? "inset 0 0 0 1px #FEC84B" : "none",
        transition: "background-color .2s",
      }}
    >
      <Typography component="div" sx={{ fontSize: "12px", lineHeight: "16px", fontWeight: 500, color: "#667085" }}>
        <Highlight>{label}</Highlight>
      </Typography>
      <Typography
        component="div"
        sx={{
          mt: "1px",
          fontSize: "14px",
          lineHeight: "20px",
          fontWeight: bold ? 700 : 600,
          color: tone === "ok" ? "#067647" : tone === "warn" ? "#B54708" : empty ? "#98A2B3" : "#101828",
          overflowWrap: "anywhere",
        }}
      >
        <Highlight>{shown}</Highlight>
      </Typography>
    </Box>
  );
};

/** The page under its search term: the page's own render is not what a new term triggers. */
const ViewOnlinePolicyPage = (props) => (
  <SearchProvider>
    <ViewOnlinePolicy {...props} />
  </SearchProvider>
);

export default ViewOnlinePolicyPage;
